import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { RealtimeChannel } from '@supabase/supabase-js';

import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthenticationContext';
import { useClinic } from '@/contexts/ClinicContext';
import { useDentist } from '@/contexts/DentistContext';
import {
  DentistNotification,
  getDentistNotifications,
  getDentistNotificationsCount,
  getUnreadNotificationsCount,
  readNotifications
} from '@/data/DentistNotifications';
import { Dentist } from '@/data/Dentists';
import { useToast } from '@/hooks';
import { NOTIFICATIONS_PAGE_SIZE, POPUP_IDENTIFIERS } from '@/utils/constants';
import { filterValidNotifications } from '@/utils/helpers/filterValidNotifications';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { supabase } from '@/utils/supabase';
import { ensureMinimumDelay } from '@/utils/timing';
import {
  UseQueryResult,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { compareDesc, format, formatISO } from 'date-fns';
import { useLocation } from 'react-router-dom';

interface NotificationsContextType {
  isNotificationsOpen: boolean;
  setIsNotificationsOpen: (isNotificationsOpen: boolean) => void;
  notificationsPresentingElement: HTMLElement | undefined;
  setNotificationsPresentingElement: (
    notificationsPresentingElement: HTMLElement | undefined
  ) => void;
  onOpenNotifications: () => void;
  onCloseNotifications: () => void;
  notifications: DentistNotification[];
  fetchNotifications: () => void;
  fetchMoreNotifications: () => Promise<void>;
  loading: boolean;
  loaded: boolean;
  loadingCount: boolean;
  loadedCount: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  groupedNotifications: {
    unread: DentistNotification[];
    read: DentistNotification[];
    readByDate: Record<string, DentistNotification[]>;
  };
  readNotification: () => Promise<void>;
  unreadCount: number;
  fetchUnreadCount: () => void;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { authenticated } = useAuth();
  const { isClinic } = useClinic();
  const { selectedDentist: dentist, setDentists } = useDentist();
  const { isPaused, isResumed } = useApp();
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const { search } = useLocation();
  const popupFromUrl = new URLSearchParams(search).get('popup');

  const [isNotificationsOpen, setIsNotificationsOpen] =
    useState<boolean>(false);
  const [notificationsPresentingElement, setNotificationsPresentingElement] =
    useState<HTMLElement | undefined>(undefined);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [fetchedCount, setFetchedCount] = useState<number>(0);
  const [useDentistBadge, setUseDentistBadge] = useState<boolean>(true);

  const {
    data: totalCount = 0,
    isLoading: loadingCount,
    isFetched: loadedCount
  }: UseQueryResult<number, Error> = useQuery({
    queryKey: ['notificationsCount', dentist?.id],
    queryFn: async () => {
      if (!dentist?.id) return 0;
      return await getDentistNotificationsCount(dentist.id!);
    },
    enabled: isNotificationsOpen && !!dentist?.id
  });

  const {
    data: unreadCount = 0,
    refetch: fetchUnreadCount
  }: UseQueryResult<number, Error> = useQuery({
    queryKey: ['unreadNotificationsCount', dentist?.id],
    queryFn: async () => {
      if (!dentist?.id) return 0;

      // Use dentist badge for first load to save on API requests
      if (useDentistBadge) {
        setUseDentistBadge(false);
        return dentist?.badge ?? 0;
      }

      return await getUnreadNotificationsCount({ dentistId: dentist.id! });
    },
    enabled: false
  });

  const {
    data: notifications = [],
    isLoading: loading,
    isFetched: loaded,
    refetch: fetchNotifications,
    isRefetching: reloading
  }: UseQueryResult<DentistNotification[], Error> = useQuery({
    queryKey: ['notifications', dentist?.id],
    queryFn: async () => {
      try {
        if (totalCount === 0) return [];

        const fetchStartTime = Date.now();
        const pageSize = NOTIFICATIONS_PAGE_SIZE * 2;
        let page = 0;
        let hasMorePages = true;
        let allValidNotifications: DentistNotification[] = [];
        let totalNotificationsFetched = 0;

        // Continue fetching until we have enough valid notifications or exhaust all pages
        while (hasMorePages && allValidNotifications.length < pageSize) {
          const pageOffset = page * pageSize;

          const pageNotifications = await queryClient.fetchQuery({
            queryKey: ['notifications', dentist?.id!, 'page', page],
            queryFn: () =>
              getDentistNotifications({
                dentistId: dentist?.id!,
                limit: pageSize,
                offset: pageOffset
              })
          });

          totalNotificationsFetched += pageNotifications.length;
          hasMorePages = totalNotificationsFetched < totalCount;

          const validNotifications =
            filterValidNotifications(pageNotifications);
          const uniqueNotifications = validNotifications.filter(
            (notification: DentistNotification) =>
              notification.id &&
              allValidNotifications.findIndex(
                (n) => n.id === notification.id
              ) === -1
          );

          allValidNotifications.push(...uniqueNotifications);
          page++;
        }

        setFetchedCount(totalNotificationsFetched);
        setCurrentPage(page);

        await ensureMinimumDelay(fetchStartTime);

        return allValidNotifications;
      } catch {
        displayToast({
          message: 'Please check your internet connection.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
        return [];
      }
    },
    enabled: !!dentist?.id && isNotificationsOpen && loadedCount
  });

  const fetchMoreNotifications = useCallback(async () => {
    if (!dentist?.id || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const fetchStartTime = Date.now();
      const pageSize = NOTIFICATIONS_PAGE_SIZE;
      let page = currentPage;
      let hasMorePages: boolean = hasMore;
      let validNotifications: DentistNotification[] = [];
      let totalNotificationsFetched = fetchedCount;

      // Continue fetching until we find valid notifications or exhaust all pages
      while (hasMorePages && validNotifications.length === 0) {
        const pageOffset = page * pageSize;

        const pageNotifications = await queryClient.fetchQuery({
          queryKey: ['notifications', dentist.id!, 'page', page],
          queryFn: () =>
            getDentistNotifications({
              dentistId: dentist.id!,
              limit: pageSize,
              offset: pageOffset
            })
        });

        totalNotificationsFetched += pageNotifications.length;
        hasMorePages = totalNotificationsFetched < totalCount;

        validNotifications = filterValidNotifications(pageNotifications);
        page++;
      }

      setFetchedCount((prev) => prev + totalNotificationsFetched);
      setCurrentPage(page);

      await ensureMinimumDelay(fetchStartTime);

      queryClient.setQueryData(
        ['notifications', dentist.id],
        (old: DentistNotification[] = []) => {
          const newNotifications = validNotifications.filter(
            (notification: DentistNotification) =>
              notification.id &&
              old.findIndex(
                (n: DentistNotification) => n.id === notification.id
              ) === -1
          );
          return [...old, ...newNotifications];
        }
      );
    } catch {
      displayToast({
        message: 'Please check your internet connection.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    } finally {
      setLoadingMore(false);
    }
  }, [
    dentist?.id,
    currentPage,
    hasMore,
    loadingMore,
    queryClient,
    fetchedCount,
    totalCount,
    displayToast
  ]);

  const stream = useCallback(async () => {
    if (channel) supabase.removeChannel(channel);

    if (!dentist?.id || (isPaused && !isResumed)) {
      setChannel(null);
      return;
    }

    const channelName = `notifications${dentist.id?.replace(
      /-/g,
      ''
    )}${Math.floor(Date.now() / 1000)}`;

    const newChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'dentist_notifications',
          filter: `dentist_id=eq.${dentist.id}`
        },
        async (payload) => {
          const newNotification = payload.new as DentistNotification;
          const request = newNotification.request;

          if (!request) {
            queryClient.setQueryData(
              ['notifications', dentist.id],
              (old: DentistNotification[] = []) => [...old, newNotification]
            );
            queryClient.setQueryData(
              ['unreadNotificationsCount', dentist.id],
              (old: number = 0) => old + 1
            );
            return;
          }

          const validNotifications = filterValidNotifications([
            newNotification
          ]);
          if (validNotifications.length === 0) return;

          queryClient.setQueryData(
            ['notifications', dentist.id],
            (old: DentistNotification[] = []) => [...old, newNotification]
          );

          queryClient.setQueryData(
            ['unreadNotificationsCount', dentist.id],
            (old: number = 0) => old + 1
          );
        }
      )
      .subscribe();

    setChannel(newChannel);
  }, [dentist?.id, queryClient, isPaused, isResumed]);

  const readNotification = useCallback(async () => {
    if (dentist?.id) {
      try {
        const unreadNotifications = notifications.filter(
          (notification) => !notification.read_at
        );

        if (unreadNotifications.length === 0) return;

        await readNotifications(dentist.id!);

        queryClient.setQueryData(['notifications', dentist.id], () => {
          return notifications.map((notification) => ({
            ...notification,
            read_at: formatISO(now())
          }));
        });
        queryClient.setQueryData(
          ['unreadNotificationsCount', dentist.id],
          () => {
            return 0;
          }
        );
      } finally {
        setDentists((previousDentists: Dentist[]) => {
          return previousDentists.map((d: Dentist) =>
            d.id === dentist?.id ? { ...d, badge: 0 } : d
          );
        });
      }
    }
  }, [dentist?.id, notifications, isClinic, queryClient]);

  const onOpenNotifications = useCallback(() => {
    setIsNotificationsOpen(true);
  }, []);

  const onCloseNotifications = useCallback(() => {
    setIsNotificationsOpen(false);
    readNotification();
  }, [readNotification]);

  const groupedNotifications = useMemo(() => {
    const unreadNotifications = notifications
      .filter((notification) => !notification.read_at)
      .sort((a, b) => {
        if (!a.created_at || !b.created_at) return 0;
        return compareDesc(parseDate(a.created_at), parseDate(b.created_at));
      });

    const readNotifications = notifications.filter(
      (notification) => notification.read_at
    );

    const readNotificationsByDate = readNotifications.reduce(
      (acc, notification) => {
        if (notification.created_at) {
          const date = format(parseDate(notification.created_at), 'yyyy-MM-dd');
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push(notification);
        }
        return acc;
      },
      {} as Record<string, DentistNotification[]>
    );

    Object.keys(readNotificationsByDate).forEach((date) => {
      readNotificationsByDate[date].sort((a, b) => {
        if (!a.created_at || !b.created_at) return 0;
        return compareDesc(parseDate(a.created_at), parseDate(b.created_at));
      });
    });

    const sortedReadNotificationsByDate = Object.keys(readNotificationsByDate)
      .sort((a, b) => compareDesc(parseDate(a), parseDate(b)))
      .reduce(
        (acc, date) => {
          acc[date] = readNotificationsByDate[date];
          return acc;
        },
        {} as Record<string, DentistNotification[]>
      );

    return {
      unread: unreadNotifications,
      read: readNotifications,
      readByDate: sortedReadNotificationsByDate
    };
  }, [notifications]);

  useEffect(() => {
    if (
      authenticated &&
      dentist &&
      notificationsPresentingElement &&
      popupFromUrl &&
      popupFromUrl === POPUP_IDENTIFIERS.NOTIFICATIONS &&
      !loading &&
      loaded
    ) {
      setIsNotificationsOpen(true);
    }
  }, [
    authenticated,
    dentist,
    notificationsPresentingElement,
    popupFromUrl,
    loading,
    loaded
  ]);

  useEffect(() => {
    if (totalCount > 0 && fetchedCount > 0) {
      setHasMore(fetchedCount < totalCount);
    }
  }, [totalCount, fetchedCount]);

  useEffect(() => {
    stream();
  }, [stream]);

  useEffect(() => {
    if (!isNotificationsOpen) {
      fetchUnreadCount();
    }
  }, [isNotificationsOpen, fetchUnreadCount]);

  useEffect(() => {
    if (!dentist?.id) {
      setUseDentistBadge(true);
    }

    return () => {
      setUseDentistBadge(true);
    };
  }, [dentist?.id]);

  return (
    <NotificationsContext.Provider
      value={{
        isNotificationsOpen,
        setIsNotificationsOpen,
        notificationsPresentingElement,
        setNotificationsPresentingElement,
        onOpenNotifications,
        onCloseNotifications,
        notifications,
        fetchNotifications,
        fetchMoreNotifications,
        loading: loading || reloading,
        loaded,
        loadingCount,
        loadedCount,
        loadingMore,
        hasMore,
        groupedNotifications,
        readNotification,
        unreadCount,
        fetchUnreadCount
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationsProvider'
    );
  }
  return context;
};
